import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ArrowLeft, Check, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { formatMinutes, formatDecimalHours, formatWeekLabel, formatTimeInTz } from "@/lib/timeFormat";
import BreakViolationIcon from "@/components/timesheet/BreakViolationIcon";
import BreakOverrideDialog from "@/components/approver/BreakOverrideDialog";
import WeeklyBreakdown from "@/components/timesheet/WeeklyBreakdown";
import { useRuntimeEngines } from "@/lib/runtimeEngines";
import { useAuth } from "@/lib/auth";

export default function TimesheetDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ts, setTs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [overrideEntry, setOverrideEntry] = useState(null);
  const engines = useRuntimeEngines();
  const { isApprover, hasRole } = useAuth();
  const canOverride = isApprover || hasRole(["admin", "tenant_admin"]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/approvals/timesheets/${id}`);
      setTs(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doApprove = async () => {
    setBusy(true);
    try {
      await api.post(`/approvals/timesheets/${id}/approve`, {});
      toast.success("Approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const doReject = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/approvals/timesheets/${id}/reject`, { reason });
      toast.success("Rejected. Worker notified.");
      setRejectOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const doRevoke = async () => {
    setBusy(true);
    try {
      await api.post(`/approvals/timesheets/${id}/revoke`);
      toast.success("Approval revoked");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading || !ts) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const isSubmitted = ts.status === "submitted";
  const isApproved = ts.status === "approved";
  const isRejected = ts.status === "rejected";

  return (
    <div className="space-y-6" data-testid="timesheet-detail-page">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="mb-2 -ml-3" data-testid="detail-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Approvals</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">{ts.worker_name}</h1>
          <p className="mt-2 text-muted-foreground">
            {formatWeekLabel(ts.period_start_local, ts.period_end_local)}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusPill status={ts.status} />
          {isSubmitted && (
            <div className="flex gap-2">
              <Button onClick={doApprove} disabled={busy} data-testid="detail-approve-btn">
                <Check className="h-4 w-4" /> Approve
              </Button>
              <Button variant="destructive" onClick={() => setRejectOpen(true)} disabled={busy} data-testid="detail-reject-btn">
                <X className="h-4 w-4" /> Reject
              </Button>
            </div>
          )}
          {isApproved && (
            <Button variant="outline" onClick={doRevoke} disabled={busy} data-testid="detail-revoke-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Revoke approval
            </Button>
          )}
        </div>
      </div>

      {isRejected && ts.rejection_reason && (
        <Alert variant="destructive" className="border-destructive/40 bg-destructive/5">
          <AlertDescription>
            <span className="font-semibold">Rejection reason: </span>
            <span className="italic">{ts.rejection_reason}</span>
          </AlertDescription>
        </Alert>
      )}

      {ts.checklist_required_unchecked > 0 && (
        <Alert className="border-amber-500/40 bg-amber-500/10" data-testid="checklist-warning">
          <AlertDescription>
            <span className="font-semibold text-amber-700">Heads up: </span>
            <span>{ts.checklist_required_unchecked} required checklist item{ts.checklist_required_unchecked === 1 ? "" : "s"} unchecked across this week's entries.</span>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <SummaryCard label="Total hours" value={`${formatMinutes(ts.total_minutes)} (${formatDecimalHours(ts.total_minutes)}h)`} />
        <SummaryCard label="REG" value={formatMinutes(ts.pay_summary?.totals?.reg_minutes || 0)} tone="primary" />
        {/* Phase 27b — hide OT/DT tiles on plain weeks (both zero). */}
        {((ts.pay_summary?.totals?.ot_minutes || 0) > 0
          || (ts.pay_summary?.totals?.dt_minutes || 0) > 0) && (
          <>
            <SummaryCard label="OT" value={formatMinutes(ts.pay_summary?.totals?.ot_minutes || 0)} tone="amber" />
            <SummaryCard label="DT" value={formatMinutes(ts.pay_summary?.totals?.dt_minutes || 0)} tone="destructive" />
          </>
        )}
        <SummaryCard label="PTO" value={formatMinutes(ts.pay_summary?.totals?.pto_minutes || 0)} tone="secondary" />
        <SummaryCard label="Source" value={<span className="capitalize">{ts.submission_source || "worker"}</span>} />
      </div>

      {ts.pay_summary?.policy && (
        <div className="text-xs text-muted-foreground" data-testid="detail-pay-policy">
          Policy: <span className="font-mono uppercase">{ts.pay_summary.policy.name}</span>
          {ts.pay_summary.policy.daily_ot_threshold_hours && <> · daily OT after {ts.pay_summary.policy.daily_ot_threshold_hours}h</>}
          {ts.pay_summary.policy.weekly_ot_threshold_hours && <> · weekly OT after {ts.pay_summary.policy.weekly_ot_threshold_hours}h</>}
          {ts.pay_summary.policy.seventh_day_rule_enabled && <> · 7th-day rule ON</>}
        </div>
      )}

      {engines.ot && (
        <WeeklyBreakdown paySummary={ts.pay_summary} testid="detail-breakdown" />
      )}

      <Card>
        <CardHeader><CardTitle>Hours by day</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2">
            {Object.entries(ts.hours_by_day || {}).sort().map(([date, mins]) => (
              <div key={date} className="rounded-lg border p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{new Date(date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" })}</div>
                <div className="mt-1 font-display text-lg tabular-nums">{formatMinutes(mins)}</div>
                <div className="text-[10px] text-muted-foreground">{new Date(date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
              </div>
            ))}
            {(!ts.hours_by_day || Object.keys(ts.hours_by_day).length === 0) && (
              <div className="col-span-7 text-center text-sm text-muted-foreground">No entries.</div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Entries ({(ts.entries || []).length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(ts.entries || []).map((e) => {
            const requiredTotal = (e.checklist_completions || []).reduce(
              (n, c) => n + (c.snapshot_items || []).filter((i) => i.required).length, 0);
            const requiredChecked = (e.checklist_completions || []).reduce(
              (n, c) => n + (c.snapshot_items || []).filter((i) => i.required && (c.checked_item_ids || []).includes(i.item_id)).length, 0);
            const requiredUnchecked = requiredTotal - requiredChecked;
            return (
            <div key={e.id} className="rounded-lg border p-3 text-sm" data-testid={`detail-entry-${e.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-medium">
                      {e.source === "pto"
                        ? <span className="italic">PTO · {formatMinutes(e.duration_minutes)}</span>
                        : e.entry_mode === "duration"
                        ? <span>Hours entered · {formatMinutes(e.duration_minutes)}</span>
                        : <>{formatTimeInTz(e.clock_in_at, "America/New_York")} → {formatTimeInTz(e.clock_out_at, "America/New_York")}
                            <span className="ml-2 text-muted-foreground">· {formatMinutes(e.duration_minutes)}</span></>
                      }
                    </div>
                    {engines.break && e.source !== "pto" && (
                      <BreakViolationIcon
                        entry={e}
                        role={canOverride ? "approver" : "worker"}
                        onApproverOverride={canOverride ? setOverrideEntry : undefined}
                        onNoteSaved={load}
                      />
                    )}
                  </div>
                  {e.source !== "pto" && e.raw_minutes != null && (e.unpaid_break_minutes + e.auto_deducted_minutes) > 0 && (
                    <div className="mt-1 text-xs font-mono text-muted-foreground">
                      gross {formatMinutes(e.raw_minutes)} − {formatMinutes(e.unpaid_break_minutes + e.auto_deducted_minutes)} break{e.auto_deducted_minutes > 0 ? " (auto-deduct)" : ""} = net {formatMinutes(e.duration_minutes || 0)}
                    </div>
                  )}
                  {e.notes && <div className="mt-1 text-muted-foreground">{e.notes}</div>}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge variant="outline" className="rounded-full text-[10px] uppercase">{e.source}</Badge>
                  {requiredTotal > 0 && (
                    <Badge className={`rounded-full text-[10px] ${requiredUnchecked === 0 ? "bg-success/15 text-success" : "bg-amber-500/15 text-amber-700"}`}>
                      {requiredUnchecked === 0 ? `✓ all ${requiredTotal} required` : `${requiredUnchecked} required unchecked`}
                    </Badge>
                  )}
                </div>
              </div>
              {(e.checklist_completions || []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {e.checklist_completions.map((c) => (
                    <div key={c.checklist_id} className="rounded border bg-muted/30 p-2 text-xs">
                      <div className="mb-1 font-medium">{c.checklist_name || "Checklist"}</div>
                      <div className="flex flex-wrap gap-1">
                        {(c.snapshot_items || []).map((it) => {
                          const done = (c.checked_item_ids || []).includes(it.item_id);
                          return (
                            <Badge key={it.item_id} variant="outline" className={`rounded-full ${done ? "border-success text-success" : it.required ? "border-amber-500 text-amber-700" : "text-muted-foreground"}`}>
                              {done ? "✓" : "·"} {it.label}{it.required && !done ? " *" : ""}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );})}
          {(!ts.entries || ts.entries.length === 0) && <div className="text-sm text-muted-foreground">No entries.</div>}
        </CardContent>
      </Card>

      {(ts.history || []).length > 0 && (
        <Card>
          <CardHeader><CardTitle>Audit trail</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {ts.history.map((h, i) => (
              <div key={i} className="flex items-start justify-between border-b py-2 last:border-b-0">
                <div>
                  <span className="font-medium capitalize">{h.event.replace(/_/g, " ")}</span>
                  {h.actor_via_magic_link && <Badge variant="outline" className="ml-2 rounded-full text-[10px]">via magic link</Badge>}
                  {h.metadata?.reason && <div className="mt-1 italic text-muted-foreground">Reason: {h.metadata.reason}</div>}
                  {h.metadata?.note && <div className="mt-1 italic text-muted-foreground">Note: {h.metadata.note}</div>}
                </div>
                <div className="text-muted-foreground">{new Date(h.at).toLocaleString()}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={rejectOpen} onOpenChange={(v) => !v && setRejectOpen(false)}>
        <DialogContent data-testid="detail-reject-dialog">
          <DialogHeader><DialogTitle>Reject timesheet</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Reason (required)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} data-testid="detail-reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doReject} disabled={busy || !reason.trim()} data-testid="detail-reject-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {engines.break && (
        <BreakOverrideDialog
          entry={overrideEntry}
          open={!!overrideEntry}
          onClose={() => setOverrideEntry(null)}
          onOverridden={() => { setOverrideEntry(null); load(); }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    submitted: "bg-blue-500/15 text-blue-700",
    approved: "bg-success/15 text-success",
    rejected: "bg-destructive/15 text-destructive",
    revised: "bg-amber-500/15 text-amber-700",
    draft: "bg-muted text-muted-foreground",
  };
  return <Badge className={`rounded-full capitalize ${map[status] || map.draft}`}>{status}</Badge>;
}

function SummaryCard({ label, value, tone = "primary" }) {
  const toneClass = {
    primary: "text-primary",
    amber: "text-amber-600",
    destructive: "text-destructive",
    secondary: "text-secondary",
  }[tone] || "text-primary";
  return (
    <Card className="card-elevate">
      <CardContent className="pt-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`mt-2 font-display text-2xl font-semibold ${toneClass}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
